import React, { useState } from 'react';
import styled from '@emotion/native';
import {
  Image,
  Linking,
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import {
  BaseText,
  BodyText,
  H3,
  LoraText,
} from '@/shared/components/StyledText';
import Button from '@/shared/components/Button';
import {
  accent,
  cancelledRed,
  green,
  lightGreen,
  mint,
  mintGreen,
  mutedGreen,
  pillBrown,
  sand,
  subheadline,
  white,
} from '@/shared/styles/colors';
import { BOX_SHADOW_STYLE } from '@/shared/styles/constants';

export type Tone = 'good' | 'attention' | 'problem' | 'neutral';

const TONES: Record<Tone, { background: string; text: string }> = {
  good: { background: green, text: white },
  attention: { background: accent, text: green },
  problem: { background: cancelledRed, text: white },
  neutral: { background: pillBrown, text: green },
};

/** Only problems get color; fine is plain (borrowed from bg-health) */
const isOff = (tone: Tone) => tone === 'attention' || tone === 'problem';

export const Pill = ({ text, tone }: { text: string; tone: Tone }) => (
  <PillView style={{ backgroundColor: TONES[tone].background }}>
    <PillText style={{ color: TONES[tone].text }}>{text}</PillText>
  </PillView>
);

const PillView = styled.View({
  alignSelf: 'flex-start',
  paddingHorizontal: 9,
  height: 24,
  justifyContent: 'center',
});

const PillText = styled(BaseText)({
  fontSize: 11,
  fontWeight: 'bold',
  letterSpacing: 0.6,
  textTransform: 'uppercase',
});

export const StatusDot = ({ tone }: { tone: Tone }) =>
  isOff(tone) ? (
    <View
      style={{
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: TONES[tone].background,
      }}
    />
  ) : null;

/** Mint panel, like session cards and the availability editor */
export const Panel = styled.View({
  backgroundColor: mint,
  borderRadius: 8,
  padding: 20,
  ...BOX_SHADOW_STYLE,
});

/** White row on a mint panel */
export const RowCard = styled.View<{ isDesktop: boolean }>(({ isDesktop }) => ({
  backgroundColor: white,
  borderRadius: 8,
  paddingVertical: 14,
  paddingHorizontal: isDesktop ? 18 : 16,
  flexDirection: isDesktop ? 'row' : 'column',
  alignItems: isDesktop ? 'center' : 'flex-start',
  gap: isDesktop ? 20 : 8,
  marginBottom: 8,
}));

export const SectionTitle = styled(H3)({
  fontSize: 22,
  lineHeight: 26,
  letterSpacing: -0.2,
  textTransform: 'uppercase',
  marginBottom: 14,
});

export const Eyebrow = styled(BaseText)({
  fontSize: 11,
  letterSpacing: 1.2,
  textTransform: 'uppercase',
  fontWeight: 'bold',
  color: lightGreen,
});

export const Strong = styled(BodyText)({
  fontSize: 15,
  lineHeight: 20,
  fontWeight: 'bold',
  color: green,
});

export const Muted = styled(BodyText)({
  fontSize: 14,
  lineHeight: 19,
  color: subheadline,
});

export const Small = styled(BodyText)({
  fontSize: 12,
  lineHeight: 16,
  color: subheadline,
});

/** Underlined, so it reads as something to click */
export const ActionLink = ({
  text,
  onPress,
  accessibilityLabel,
}: {
  text: string;
  onPress: () => void;
  accessibilityLabel?: string;
}) => (
  <TouchableOpacity
    onPress={onPress}
    accessibilityRole="link"
    accessibilityLabel={accessibilityLabel ?? text}
    hitSlop={{ top: 8, bottom: 8, left: 6, right: 6 }}
  >
    <ActionLinkText>{text}</ActionLinkText>
  </TouchableOpacity>
);

const ActionLinkText = styled(BaseText)({
  fontSize: 13,
  fontWeight: 'bold',
  color: green,
  textDecorationLine: 'underline',
});

export const EmailLink = ({ email }: { email?: string | null }) =>
  email ? (
    <TouchableOpacity
      onPress={() => Linking.openURL(`mailto:${email}`)}
      accessibilityRole="link"
      accessibilityLabel={`Email ${email}`}
    >
      <Small numberOfLines={1} style={{ textDecorationLine: 'underline' }}>
        {email}
      </Small>
    </TouchableOpacity>
  ) : null;

/**
 * The page's answer first (borrowed from bg-health's "Today" card): one
 * line, then only the things that need her, each opening the right view.
 */
export const Headline = ({
  title,
  items,
  allClear,
}: {
  title: string;
  items: {
    key: string;
    tone: Tone;
    text: string;
    detail?: string;
    actionText?: string;
    onPress?: () => void;
  }[];
  allClear: string;
}) => (
  <HeadlineCard>
    <HeadlineTitle>{title}</HeadlineTitle>
    {items.length === 0 ? (
      <Muted style={{ marginTop: 6 }}>{allClear}</Muted>
    ) : (
      items.map((item) => (
        <HeadlineRow key={item.key}>
          <View style={{ paddingTop: 7 }}>
            <StatusDot tone={item.tone} />
          </View>
          <View style={{ flex: 1, gap: 2 }}>
            <Strong>{item.text}</Strong>
            {item.detail ? <Muted>{item.detail}</Muted> : null}
          </View>
          {item.onPress ? (
            <ActionLink
              text={item.actionText ?? 'View'}
              onPress={item.onPress}
            />
          ) : null}
        </HeadlineRow>
      ))
    )}
  </HeadlineCard>
);

const HeadlineCard = styled.View({
  backgroundColor: white,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: mint,
  paddingVertical: 22,
  paddingHorizontal: 24,
  ...BOX_SHADOW_STYLE,
});

const HeadlineTitle = styled(LoraText)({
  fontSize: 28,
  lineHeight: 34,
  color: green,
  textTransform: 'uppercase',
  marginBottom: 8,
});

const HeadlineRow = styled.View({
  flexDirection: 'row',
  alignItems: 'flex-start',
  gap: 12,
  paddingVertical: 12,
  borderTopWidth: 1,
  borderTopColor: mint,
});

/** Weekly bars, latest in green, for a quick sense of direction */
export const MiniBars = ({
  values,
  label,
  describe = String,
}: {
  values: number[];
  label: string;
  describe?: (value: number) => string;
}) => {
  const max = Math.max(...values, 1);
  // Flat dashes read as a broken chart; say it instead
  if (values.every((value) => value === 0)) {
    return (
      <Small style={{ marginTop: 14 }}>
        {label.replace(/^[^,]+,\s*/, 'None in the ')}
      </Small>
    );
  }
  return (
    <View
      accessible
      accessibilityLabel={`${label}: ${values.map(describe).join(', ')}`}
      style={{ marginTop: 14 }}
    >
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-end',
          gap: 4,
          height: 30,
        }}
      >
        {values.map((value, index) => (
          <View
            // Fixed-length series, so the index is the week
            // eslint-disable-next-line react/no-array-index-key
            key={index}
            style={{
              flex: 1,
              height: value > 0 ? Math.max(3, (value / max) * 30) : 2,
              borderRadius: 2,
              backgroundColor: index === values.length - 1 ? green : mintGreen,
            }}
          />
        ))}
      </View>
      <Small style={{ marginTop: 6 }}>{label}</Small>
    </View>
  );
};

export const MetricCard = ({
  label,
  value,
  sub,
  compare,
  tone = 'neutral',
  bars,
  onPress,
}: {
  label: string;
  value: string;
  sub?: string;
  /** e.g. "vs 5 this time last month" */
  compare?: string;
  tone?: Tone;
  bars?: React.ReactNode;
  onPress?: () => void;
}) => (
  <TouchableOpacity
    onPress={onPress}
    disabled={!onPress}
    accessibilityRole={onPress ? 'button' : undefined}
    style={{ flex: 1, minWidth: 0 }}
  >
    <MetricCardView>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
        <StatusDot tone={tone} />
        <Eyebrow style={{ flex: 1 }}>{label}</Eyebrow>
        {onPress ? <Eyebrow>›</Eyebrow> : null}
      </View>
      <StatValue>{value}</StatValue>
      {sub ? <Muted>{sub}</Muted> : null}
      {compare ? <Small style={{ marginTop: 2 }}>{compare}</Small> : null}
      {bars}
    </MetricCardView>
  </TouchableOpacity>
);

const MetricCardView = styled.View({
  flex: 1,
  backgroundColor: white,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: mint,
  paddingTop: 18,
  paddingBottom: 18,
  paddingHorizontal: 20,
  ...BOX_SHADOW_STYLE,
});

const StatValue = styled(LoraText)({
  fontSize: 38,
  lineHeight: 44,
  color: green,
  marginTop: 8,
  marginBottom: 2,
});

/** Fixed columns, so cards never wrap into uneven rows */
export const Grid = ({
  columns,
  gap = 16,
  stretch = true,
  children,
}: {
  columns: number;
  gap?: number;
  /** Equal heights per row; off for panels whose lengths vary a lot */
  stretch?: boolean;
  children: React.ReactNode;
}) => {
  const items = React.Children.toArray(children).filter(Boolean);
  const rows: React.ReactNode[][] = [];
  for (let i = 0; i < items.length; i += columns) {
    rows.push(items.slice(i, i + columns));
  }
  return (
    <View style={{ gap }}>
      {rows.map((row, rowIndex) => (
        // eslint-disable-next-line react/no-array-index-key
        <View
          key={rowIndex}
          style={{
            flexDirection: 'row',
            gap,
            alignItems: stretch ? 'stretch' : 'flex-start',
          }}
        >
          {row}
          {/* Keep a short last row the same column widths */}
          {Array.from({ length: columns - row.length }, (_, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <View key={`spacer-${index}`} style={{ flex: 1 }} />
          ))}
        </View>
      ))}
    </View>
  );
};

export const FilterChips = <T extends string>({
  options,
  selected,
  onSelect,
}: {
  options: { value: T; label: string; count?: number | null }[];
  selected: T;
  onSelect: (value: T) => void;
}) => (
  <ChipRow>
    {options.map((option) => {
      const isSelected = option.value === selected;
      return (
        <TouchableOpacity
          key={option.value}
          onPress={() => onSelect(option.value)}
          accessibilityRole="button"
          accessibilityState={{ selected: isSelected }}
        >
          <Chip
            style={{
              backgroundColor: isSelected ? green : white,
              borderColor: isSelected ? green : mutedGreen,
            }}
          >
            <ChipText style={{ color: isSelected ? white : green }}>
              {option.label}
              {option.count !== undefined && option.count !== null
                ? ` · ${option.count}`
                : ''}
            </ChipText>
          </Chip>
        </TouchableOpacity>
      );
    })}
  </ChipRow>
);

const ChipRow = styled.View({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: 8,
});

const Chip = styled.View({
  borderRadius: 187,
  borderWidth: 1,
  paddingHorizontal: 16,
  height: 34,
  justifyContent: 'center',
});

const ChipText = styled(BaseText)({
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: 0.4,
});

/**
 * A labelled setting (who, when) rather than a list view, so it looks
 * different from the chips and the tabs: white track, smaller pills.
 */
export const SegmentedControl = <T extends string>({
  label,
  options,
  selected,
  onSelect,
  disabled,
}: {
  label: string;
  options: { value: T; label: string }[];
  selected: T;
  onSelect: (value: T) => void;
  disabled?: boolean;
}) => (
  <View style={{ gap: 6, opacity: disabled ? 0.45 : 1 }}>
    <Eyebrow>{label}</Eyebrow>
    <SegmentTrack accessibilityRole="radiogroup" accessibilityLabel={label}>
      {options.map((option) => {
        const isSelected = option.value === selected;
        return (
          <TouchableOpacity
            key={option.value}
            onPress={() => onSelect(option.value)}
            disabled={disabled}
            accessibilityRole="radio"
            accessibilityState={{ checked: isSelected, disabled }}
          >
            <SegmentItem
              style={{ backgroundColor: isSelected ? green : white }}
            >
              <SegmentText style={{ color: isSelected ? white : green }}>
                {option.label}
              </SegmentText>
            </SegmentItem>
          </TouchableOpacity>
        );
      })}
    </SegmentTrack>
  </View>
);

const SegmentTrack = styled.View({
  flexDirection: 'row',
  alignSelf: 'flex-start',
  backgroundColor: white,
  borderWidth: 1,
  borderColor: mintGreen,
  borderRadius: 999,
  padding: 3,
  gap: 2,
});

const SegmentItem = styled.View({
  height: 30,
  paddingHorizontal: 14,
  borderRadius: 999,
  justifyContent: 'center',
});

const SegmentText = styled(BaseText)({
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: 0.3,
});

/** Segmented pill tabs on a sand track; scrolls sideways on phones */
export const SegmentedTabs = <T extends string>({
  tabs,
  selected,
  onSelect,
}: {
  tabs: { value: T; label: string; badge?: number | null }[];
  selected: T;
  onSelect: (value: T) => void;
}) => (
  <ScrollView
    horizontal
    showsHorizontalScrollIndicator={false}
    contentContainerStyle={{ flexGrow: 0 }}
    accessibilityRole="tablist"
  >
    <TabTrack>
      {tabs.map((tab) => {
        const isSelected = tab.value === selected;
        return (
          <TouchableOpacity
            key={tab.value}
            onPress={() => onSelect(tab.value)}
            accessibilityRole="tab"
            accessibilityState={{ selected: isSelected }}
          >
            <TabPill style={{ backgroundColor: isSelected ? green : sand }}>
              <TabText style={{ color: isSelected ? white : green }}>
                {tab.label}
              </TabText>
              {tab.badge ? (
                <Badge>
                  <BadgeText>{tab.badge}</BadgeText>
                </Badge>
              ) : null}
            </TabPill>
          </TouchableOpacity>
        );
      })}
    </TabTrack>
  </ScrollView>
);

const TabTrack = styled.View({
  flexDirection: 'row',
  backgroundColor: sand,
  borderRadius: 999,
  padding: 4,
  gap: 2,
});

const TabPill = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
  borderRadius: 999,
  height: 40,
  paddingHorizontal: 18,
});

const TabText = styled(BaseText)({
  fontSize: 13,
  fontWeight: 'bold',
  letterSpacing: 0.4,
});

const Badge = styled.View({
  minWidth: 20,
  height: 20,
  borderRadius: 10,
  paddingHorizontal: 6,
  backgroundColor: accent,
  alignItems: 'center',
  justifyContent: 'center',
});

const BadgeText = styled(BaseText)({
  fontSize: 11,
  fontWeight: 'bold',
  color: green,
});

/** Dense rows with hairlines, for long lists (Stylists, Users) */
export const TableCard = styled.View({
  backgroundColor: white,
  borderRadius: 8,
  borderWidth: 1,
  borderColor: mint,
  overflow: 'hidden',
  ...BOX_SHADOW_STYLE,
});

export type Column = { key: string; label: string; flex: number };

export const TableHeader = ({ columns }: { columns: Column[] }) => (
  <View
    style={{
      flexDirection: 'row',
      gap: 16,
      paddingVertical: 10,
      paddingHorizontal: 16,
      backgroundColor: mint,
    }}
  >
    {columns.map((column) => (
      <Eyebrow key={column.key} style={{ flex: column.flex }}>
        {column.label}
      </Eyebrow>
    ))}
  </View>
);

export const TableRow = styled.View<{ isDesktop: boolean; isFirst?: boolean }>(
  ({ isDesktop, isFirst }) => ({
    flexDirection: isDesktop ? 'row' : 'column',
    alignItems: isDesktop ? 'center' : 'flex-start',
    gap: isDesktop ? 16 : 6,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderTopWidth: isFirst ? 0 : 1,
    borderTopColor: mint,
  }),
);

export const Cell = ({
  flex,
  isDesktop,
  children,
  style,
}: {
  flex: number;
  isDesktop: boolean;
  children: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}) => (
  <View style={[isDesktop ? { flex, minWidth: 0 } : { width: '100%' }, style]}>
    {children}
  </View>
);

/**
 * Asks before something with real consequences, in place, instead of a
 * browser dialog.
 */
export const ConfirmAction = ({
  label,
  question,
  confirmText,
  onConfirm,
  disabled,
}: {
  label: string;
  question: string;
  confirmText: string;
  onConfirm: () => Promise<void> | void;
  disabled?: boolean;
}) => {
  const [asking, setAsking] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!asking) {
    return disabled ? (
      <Small>{label}</Small>
    ) : (
      <ActionLink text={label} onPress={() => setAsking(true)} />
    );
  }
  return (
    <View style={{ gap: 6 }}>
      <Small style={{ color: green }}>{question}</Small>
      <View style={{ flexDirection: 'row', gap: 14, alignItems: 'center' }}>
        <ActionLink
          text={saving ? 'Saving…' : confirmText}
          onPress={async () => {
            if (saving) return;
            setSaving(true);
            try {
              await onConfirm();
            } finally {
              setSaving(false);
              setAsking(false);
            }
          }}
        />
        <ActionLink text="Cancel" onPress={() => setAsking(false)} />
      </View>
    </View>
  );
};

export const Avatar = ({
  uri,
  name,
  size = 40,
}: {
  uri?: string | null;
  name: string;
  size?: number;
}) =>
  uri ? (
    <Image
      source={{ uri }}
      accessibilityLabel={name}
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: sand,
      }}
    />
  ) : (
    <View
      style={{
        width: size,
        height: size,
        borderRadius: size / 2,
        backgroundColor: sand,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <LoraText style={{ fontSize: size * 0.42, color: green }}>
        {name.charAt(0).toUpperCase()}
      </LoraText>
    </View>
  );

export const EmptyState = ({
  title,
  body,
  onRetry,
}: {
  title: string;
  body: string;
  onRetry?: () => void;
}) => (
  <EmptyView>
    <EmptyTitle>{title}</EmptyTitle>
    <Muted style={{ textAlign: 'center', maxWidth: 440 }}>{body}</Muted>
    {onRetry ? (
      <Button
        text="Try again"
        variant="primary"
        size="small"
        onPress={onRetry}
        customStyle={{ marginTop: 10, minWidth: 150 }}
      />
    ) : null}
  </EmptyView>
);

/** A failed load, so it's never mistaken for "nothing to show" */
export const ErrorState = ({
  what,
  onRetry,
}: {
  what: string;
  onRetry: () => void;
}) => (
  <EmptyState
    title="Couldn’t load"
    body={`Something went wrong loading ${what}. This isn’t the real picture yet.`}
    onRetry={onRetry}
  />
);

/** A failed load inside a panel */
export const ErrorNote = ({
  what,
  onRetry,
}: {
  what: string;
  onRetry: () => void;
}) => (
  <View
    style={{
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      alignItems: 'center',
    }}
  >
    <Muted>Couldn’t load {what}.</Muted>
    <ActionLink text="Try again" onPress={onRetry} />
  </View>
);

const EmptyView = styled.View({
  backgroundColor: mint,
  borderRadius: 12,
  paddingVertical: 56,
  paddingHorizontal: 24,
  alignItems: 'center',
  gap: 10,
});

const EmptyTitle = styled(H3)({
  fontSize: 22,
  textTransform: 'uppercase',
  textAlign: 'center',
});

export const Spread = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 12,
  marginBottom: 18,
});

export const Stack = styled(View)({ gap: 3 });
